import { isPaymentGatewayTicket } from './ticketRequests';
import { getProviderCategoryContext, getProviderCategoryLabels, getProviderReferenceLabel } from './providerCategory';

const TYPE_LABELS = {
  record_update: 'Record change',
  l1_create: 'Add L1 master', l0_create: 'L0 creation', add_provider: 'Add provider',
  change_provider: 'Update provider', remove_provider: 'Delete provider',
  costing_rate_change: 'L1 costing', manual: 'Manual', setup: 'Setup'
};

const dateFormat = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
const timeFormat = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' });

export const formatRate = value => {
  if (value == null || value === '') return '—';
  const rate = Number(value);
  return Number.isFinite(rate) ? `${Number.isInteger(rate) ? rate : rate.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')}%` : '—';
};

const getProviderName = provider => provider?.provider_id?.name || provider?.provider_id?.code || provider?.code || '';
export const getTicketCreatorName = ticket => (
  ticket?.activity_logs?.find(entry => entry.action === 'created')?.actor_username ||
  ticket?.provider_change?.requested_by?.username || ticket?.provider_change?.requested_by_username ||
  ticket?.costing_rate_change?.requested_by?.username || ticket?.costing_rate_change?.requested_by_username || ''
);

const displayField = (request, key, name = false) => {
  const value = String(request?.display_data?.find(field => field.key === key)?.value || '');
  const parts = value.split(' — ');
  return (name ? parts[1] || parts[0] : parts[0]).trim();
};

export const getTicketQueueItem = (ticket, fullStatusLabel) => {
  const request = ticket.l1_request || ticket.l0_request;
  const provider = ticket.provider_change?.to_master_provider_id || ticket.provider_change?.from_master_provider_id || ticket.master_provider_id;
  const providerName = getProviderName(provider) || displayField(request, 'provider_id', true);
  const connection = (request
    ? [displayField(request, 'type'), displayField(request, 'source', true), [displayField(request, 'currency'), displayField(request, 'country')].filter(Boolean).join(' / ')]
    : [provider?.type, provider?.source?.name || provider?.source?.code, [getProviderReferenceLabel(provider?.currency, true), getProviderReferenceLabel(provider?.country)].filter(Boolean).join(' / ')]).filter(Boolean);
  const { category, subCategory } = getProviderCategoryLabels(provider);
  const context = [...connection, !request && provider && getProviderCategoryContext(provider)].filter(Boolean).join(' · ');
  let title = ticket.title;
  const merchant = ticket.merchant_id;
  const providerLabel = [providerName, provider?.type, provider?.source?.name || provider?.source?.code,
    getProviderReferenceLabel(provider?.currency, true), getProviderReferenceLabel(provider?.country)].filter(Boolean).join(' / ');
  const action = { add_provider: 'Add Provider', change_provider: 'Update Provider', remove_provider: 'Delete Provider' }[ticket.ticket_type];
  const recordLabel = request && ['provider_id', 'type', 'source', 'currency', 'country']
    .map(key => request.display_data?.find(field => field.key === key)?.value).filter(Boolean).join(' / ');
  const generatedTitle = action ? `${action} - ${merchant?.name || merchant?.merchant_code} - ${providerLabel}`
    : ticket.ticket_type === 'costing_rate_change' ? `L1 Costing Rate Change - ${providerLabel}`
    : request ? `New ${ticket.ticket_type === 'l1_create' ? 'L1' : 'L0'} ${request.label}${(recordLabel || request.data?.name) ? ` - ${recordLabel || request.data.name}` : ''}` : '';
  if (title === generatedTitle) title = providerName || request?.data?.name || title;
  if ((ticket.ticket_type || 'setup') === 'setup' && providerName) {
    const merchantLabel = [merchant?.name, merchant?.merchant_code || merchant?.client_code].filter(Boolean).join(' / ');
    if (title === `${merchantLabel} - ${providerName}`) title = providerName;
  }
  const typeLabel = isPaymentGatewayTicket(ticket)
    ? ticket.ticket_type === 'l1_create' ? 'New L1 payment gateway' : ({ add_provider: 'Assign payment gateway', change_provider: 'Update payment gateway', remove_provider: 'Remove payment gateway' }[ticket.ticket_type] || TYPE_LABELS[ticket.ticket_type])
    : TYPE_LABELS[ticket.ticket_type] || 'Setup';
  const detail = ticket.ticket_type === 'costing_rate_change'
    ? `${formatRate(ticket.costing_rate_change?.old_rate)} → ${formatRate(ticket.costing_rate_change?.requested_rate)}`
    : context || (request ? `New ${request.label || 'record'}` : ticket.description || '');
  const statusLabel = fullStatusLabel || ticket.workflow?.current_status_label || String(ticket.status || 'Open').replace(/_/g, ' ').replace(/^./, char => char.toUpperCase());
  const status = `${ticket.workflow?.current_status || ticket.status} ${statusLabel}`.toLowerCase();
  const tone = /reject|cancel/.test(status) ? 'danger' : /block|needs info|correction/.test(status) ? 'warning'
    : /approved|submitted|completed/.test(status) ? 'success' : /review|progress|approval|applying/.test(status) ? 'active' : 'neutral';
  const updated = ticket.updated_at ? new Date(ticket.updated_at) : null;
  const validDate = updated && !Number.isNaN(updated.getTime());
  return {
    title: title || 'Untitled ticket', typeLabel, context, detail, connection, category, subCategory, statusLabel, tone,
    date: validDate ? dateFormat.format(updated) : '—', time: validDate ? timeFormat.format(updated) : '',
    fullDate: validDate ? updated.toLocaleString() : '',
    owner: ticket.assigned_to_user?.username || ticket.assigned_to_user?.email || '',
    creator: getTicketCreatorName(ticket)
  };
};
